// app/privacy/page.jsx
"use client";
import Link from "next/link";

export default function PrivacyPolicy() {
  return (
    <div className="flex-1 flex flex-col w-full px-8 sm:max-w-3xl justify-center gap-6 py-10">
      <h1 className="text-3xl font-bold text-center text-foreground">
        Privacy Policy
      </h1>
      <div className="bg-white shadow-md rounded-md p-6 border border-gray-200">
        <p className="text-sm text-gray-600 mb-4">
          Last Updated: December 12, 2024
        </p>

        <section className="mb-6">
          <p className="text-gray-700">
            Welcome to tags-for-text! We value your trust and are committed to protecting your privacy. This Privacy Policy outlines how tags-for-text collects, uses, discloses, and safeguards your personal information when you use our application for operation theater information and booking management.
          </p>
          <p className="text-gray-700 mt-2">
            By accessing or using tags-for-text, you agree to the terms of this Privacy Policy.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            1. Information We Collect
          </h2>
          <p className="text-gray-700">
            We collect various types of information to provide and improve our services, including:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li>
              <strong>1.1 Personal Information:</strong>
              <ul className="list-circle pl-6">
                <li>User Details: Name, contact information (email, phone number), address, and professional details (hospital, doctor name).</li>
                <li>Account Information: Login credentials, including username and password.</li>
                <li>Booking Information: Appointment and booking details for doctors, surgeons, and hospitals.</li>
              </ul>
            </li>
            <li>
              <strong>1.2 Non-Personal Information:</strong>
              <ul className="list-circle pl-6">
                <li>Usage data such as page views, actions taken, and session duration.</li>
                <li>Device information including IP address, browser type, operating system, and app version.</li>
              </ul>
            </li>
            <li>
              <strong>1.3 Sensitive Information:</strong>
              <ul className="list-circle pl-6">
                <li>For secure operation theater bookings and compliance: Health-related data necessary for remote monitoring, OT scheduling, and diagnosis purposes.</li>
                <li>We ensure that sensitive information is handled securely and in compliance with applicable laws.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            2. How We Use Your Information
          </h2>
          <p className="text-gray-700">
            We use the collected data for the following purposes:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li><strong>To Provide and Maintain Services:</strong> Facilitate OT booking, doctor scheduling, and hospital management; Enable AI/ML recommendations for effective OT utilization and lifecycle management.</li>
            <li><strong>For User Support and Communication:</strong> Notify you about updates, features, or changes to our services; Respond to inquiries, technical issues, or support requests.</li>
            <li><strong>For Analytics and Improvement:</strong> Understand usage trends to improve app performance and user experience; Develop new features and functionalities.</li>
            <li><strong>For Compliance and Security:</strong> Protect against unauthorized access, fraud, or misuse; Comply with legal obligations or industry standards.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            3. Sharing Your Information
          </h2>
          <p className="text-gray-700">
            We do not sell or rent your personal information. However, we may share your data under the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li><strong>With Service Providers:</strong> Third-party vendors assisting in hosting, analytics, payment processing, and other services.</li>
            <li><strong>With Hospitals and Doctors:</strong> To facilitate bookings, communication, and remote monitoring.</li>
            <li><strong>For Legal Obligations:</strong> When required to comply with legal processes, laws, or protect our rights.</li>
            <li><strong>Business Transfers:</strong> In the case of a merger, acquisition, or asset transfer.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            4. Data Storage and Security
          </h2>
          <p className="text-gray-700">
            We implement industry-standard security measures to protect your information, including:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li>Data encryption in transit and at rest.</li>
            <li>Secure servers and firewalls.</li>
            <li>Restricted access to authorized personnel only.</li>
          </ul>
          <p className="text-gray-700 mt-2">
            However, no method of transmission over the Internet or electronic storage is 100% secure.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            5. Your Rights and Choices
          </h2>
          <p className="text-gray-700">
            You have the following rights regarding your data:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li><strong>Access:</strong> Request access to the personal information we hold about you.</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete information.</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal requirements.</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications.</li>
          </ul>
          <p className="text-gray-700 mt-2">
            To exercise your rights, please contact us at{" "}
            <a href="mailto:admin@aiwoox.com​" className="text-blue-600 hover:underline">
            admin@aiwoox.com
            </a>.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            6. Cookies and Tracking Technologies
          </h2>
          <p className="text-gray-700">
            We may use cookies, web beacons, and similar technologies to:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li>Remember your preferences.</li>
            <li>Analyze usage trends and enhance user experience.</li>
          </ul>
          <p className="text-gray-700 mt-2">
            You can adjust your cookie preferences through your device settings.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            7. Third-Party Links
          </h2>
          <p className="text-gray-700">
            Our application may contain links to third-party websites or services. We are not responsible for their privacy practices. We encourage you to review their privacy policies before sharing any data.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            8. Children’s Privacy
          </h2>
          <p className="text-gray-700">
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you become aware that a child has provided us with personal information, please contact us to have it removed.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            9. Changes to This Privacy Policy
          </h2>
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time to reflect changes in our services or legal requirements. Any changes will be communicated through our app or website. Your continued use of the services indicates acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            10. Contact Us
          </h2>
          <p className="text-gray-700">
            If you have questions or concerns about this Privacy Policy, please contact us at:
          </p>
          <ul className="list-disc pl-6 text-gray-700">
            <li><strong>tags-for-text Support</strong></li>
            <li>
              Email:{" "}
              <a href="mailto:admin@aiwoox.com​" className="text-blue-600 hover:underline">
              admin@aiwoox.com​
              </a>
            </li>
            <li>Phone: <a href="tel:+918050095950" className="text-blue-600 hover:underline">+91 8050095950</a></li>
            <li>Website: <a target="_blank" href="https://tagsfortext.com/" className="text-blue-600 hover:underline">https://tagsfortext.com/</a></li>
          </ul>
        </section>

        <p className="text-gray-700 mt-4">
          By using tags-for-text, you agree to this Privacy Policy and consent to the collection, use, and disclosure of your information as described above.
        </p>

        <div className="text-center mt-6">
          <Link
            href="/login"
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}